import { useTranslation } from 'react-i18next'
import { modalStyles } from './autoRenewModalStyles'

export type ModalType = 'cancel' | 'confirm-on' | 'success' | 'cancel-success'

interface Props {
  open: boolean
  type: ModalType
  onConfirm?: () => void
  onCancel?: () => void
  onClose?: () => void
  subscriptionEndDate?: string
  cancellationDeadline?: string
  nextTermStartDate?: string
  nextTermEndDate?: string
  userEmail?: string
}

export default function AutoRenewModal({
  open,
  type,
  onConfirm,
  onCancel,
  onClose,
  subscriptionEndDate,
  cancellationDeadline,
  nextTermStartDate,
  nextTermEndDate,
  userEmail,
}: Props) {
  const { t } = useTranslation()

  if (!open) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) {
      onClose()
    }
  }

  const renderCancelModal = () => (
    <div
      style={{
        ...modalStyles.modalContainer,
        position: 'relative',
        paddingBottom: '100px',
      }}
    >
      <h2 style={modalStyles.modalTitle}>{t('AutoRenewModalCancelTitle')}</h2>

      <p style={modalStyles.modalDescription}>
        {t('AutoRenewModalCancelDescription', {
          cancellationDeadline,
          subscriptionEndDate,
          interpolation: { escapeValue: false },
        })}
      </p>

      <div style={modalStyles.buttonRow}>
        <button style={modalStyles.btnPrimary} onClick={onCancel}>
          {t('NoLabel')}
        </button>

        <button style={modalStyles.btnOutline} onClick={onConfirm}>
          {t('YesLabel')}
        </button>
      </div>

      <div style={modalStyles.modalInfo}>
        <span style={modalStyles.infoIcon}>i</span>
        {t('AutoRenewModalCancelInfo', {
          nextTermStartDate,
          cancellationDeadline,
          interpolation: { escapeValue: false },
        })}
      </div>
    </div>
  )

  const renderConfirmOnModal = () => (
    <div style={modalStyles.modalBox}>
      <h2 style={modalStyles.modalBoxTitle}>
        {t('AutoRenewModalConfirmOnTitle')}
      </h2>

      <div style={modalStyles.infoRow}>
        <span style={modalStyles.infoIcon}>i</span>

        <p style={modalStyles.infoText}>
          {t('AutoRenewModalConfirmOnDescription', {
            nextTermStartDate,
            nextTermEndDate,
            interpolation: { escapeValue: false },
          })}
        </p>
      </div>

      <div style={modalStyles.buttonRow}>
        <button style={modalStyles.btnOutline} onClick={onCancel}>
          {t('NoLabel')}
        </button>

        <button style={modalStyles.btnPrimary} onClick={onConfirm}>
          {t('YesLabel')}
        </button>
      </div>
    </div>
  )

  const renderSuccessModal = () => (
    <div style={modalStyles.successBox}>
      <div style={modalStyles.successHeader}>
        {t('AutoRenewModalSuccessHeader')}
      </div>

      <div style={modalStyles.successContent}>
        <div
          style={{
            ...modalStyles.infoRow,
            ...modalStyles.successContentInfoRow,
          }}
        >
          <span style={modalStyles.infoIcon}>i</span>

          <p style={modalStyles.successContentInfoRowP}>
            {t('AutoRenewModalSuccessRenewalInfo', {
              nextTermStartDate,
              nextTermEndDate,
              interpolation: { escapeValue: false },
            })}
          </p>
        </div>

        <div
          style={{
            ...modalStyles.infoRow,
            ...modalStyles.successContentInfoRow,
          }}
        >
          <span style={modalStyles.infoIcon}>i</span>

          <p style={modalStyles.successContentInfoRowP}>
            {t('AutoRenewModalSuccessEmailInfo', { userEmail })}
          </p>
        </div>

        <div style={modalStyles.okButtonRow}>
          <button style={modalStyles.okButtonRowBtnPrimary} onClick={onClose}>
            {t('btnOkText')}
          </button>
        </div>
      </div>
    </div>
  )

  const renderCancelSuccessModal = () => (
    <div style={modalStyles.successBox}>
      <div style={modalStyles.successHeader}>
        {t('AutoRenewModalCancelSuccessTitle')}
      </div>

      <div style={modalStyles.successContent}>
        <p style={modalStyles.successContentInfoRowP}>
          {t('AutoRenewModalCancelSuccessDescription', {
            nextTermStartDate,
            nextTermEndDate,
            interpolation: { escapeValue: false },
          })}{' '}
          {t('AutoRenewModalSuccessEmailInfo', { userEmail })}
        </p>
        <div style={modalStyles.okButtonRow}>
          <button style={modalStyles.okButtonRowBtnPrimary} onClick={onClose}>
            {t('btnOkText')}
          </button>
        </div>
      </div>
    </div>
  )

  const renderModalContent = () => {
    switch (type) {
      case 'cancel':
        return renderCancelModal()
      case 'confirm-on':
        return renderConfirmOnModal()
      case 'success':
        return renderSuccessModal()
      case 'cancel-success':
        return renderCancelSuccessModal()
      default:
        return null
    }
  }

  return (
    <div style={modalStyles.modalOverlay} onClick={handleOverlayClick}>
      {renderModalContent()}
    </div>
  )
}
